import React, { useEffect, useMemo, useState } from 'react';
import { listCashCuts, downloadCashCutPdf } from '../services/cashCutsApi';
import { getUsers } from '../services/api';
import { toast } from 'sonner';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import CashCutAuditModal from '../components/CashCutAuditModal';
import { useAuth } from '../context/AuthContext';
import {
    ListMagnifyingGlass, DownloadSimple, SpinnerGap, FileText, Warning, Money, Funnel,
} from '@phosphor-icons/react';

const fmt = (n) => new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(Number(n) || 0);
const fmtDate = (iso) => {
    if (!iso) return '—';
    try {
        return new Date(iso).toLocaleString('es-MX', {
            timeZone: 'America/Mexico_City',
            dateStyle: 'medium',
            timeStyle: 'short'
        });
    } catch { return iso; }
};

const CashCutsAuditPage = () => {
    const { can, isAdmin } = useAuth();
    const canAudit = isAdmin() || can('cash_cuts.audit_detail');

    const [cuts, setCuts] = useState([]);
    const [sellers, setSellers] = useState([]);
    const [sellerFilter, setSellerFilter] = useState('all');
    const [query, setQuery] = useState('');
    const [loading, setLoading] = useState(true);
    const [auditCutId, setAuditCutId] = useState(null);
    // "Reporte histórico" — filtros por rango de fechas + vendedor
    const [showHistorical, setShowHistorical] = useState(false);
    const [histStart, setHistStart] = useState('');
    const [histEnd, setHistEnd] = useState('');
    const [histSeller, setHistSeller] = useState('all');
    const [applying, setApplying] = useState(false);

    const load = async (params = {}) => {
        setLoading(true);
        try {
            const cutsList = await listCashCuts({
                sellerId: params.sellerId || null,
                startDate: params.startDate || null,
                endDate: params.endDate || null,
                withBreakdown: true,
            });
            setCuts(cutsList || []);
        } catch (e) {
            toast.error(e.response?.data?.detail || 'No se pudieron cargar los cortes');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        (async () => {
            try {
                const usersRes = await getUsers().catch(() => ({ data: [] }));
                setSellers((usersRes.data || []).filter(u => u.role === 'seller'));
            } catch { /* noop */ }
            await load();
        })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const applyHistorical = async () => {
        setApplying(true);
        await load({
            sellerId: histSeller === 'all' ? null : histSeller,
            startDate: histStart || null,
            endDate: histEnd || null,
        });
        setApplying(false);
    };
    const clearHistorical = async () => {
        setHistStart(''); setHistEnd(''); setHistSeller('all');
        await load();
    };

    const filteredCuts = useMemo(() => {
        return cuts.filter(c => {
            if (sellerFilter !== 'all' && c.seller_id !== sellerFilter) return false;
            if (query.trim()) {
                const q = query.trim().toLowerCase();
                if (!(c.folio || '').toLowerCase().includes(q)
                    && !(c.seller_name || '').toLowerCase().includes(q)) return false;
            }
            return true;
        });
    }, [cuts, sellerFilter, query]);

    // Totales por columna sobre los cortes visibles
    const totals = useMemo(() => {
        return filteredCuts.reduce((acc, c) => {
            const b = c.breakdown || {};
            acc.cash += Number(b.cash || 0);
            acc.credit += Number(b.credit_card || 0);
            acc.debit += Number(b.debit_card || 0);
            acc.total += Number(c.totals?.total_amount || 0);
            return acc;
        }, { cash: 0, credit: 0, debit: 0, total: 0 });
    }, [filteredCuts]);

    if (!canAudit) {
        return (
            <div className="p-6 text-center text-[#8B8474]">
                <p>No tienes permiso para ver la auditoría de cortes de caja.</p>
            </div>
        );
    }

    return (
        <div className="p-6 space-y-6" data-testid="cash-cuts-audit-page">
            <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                    <h1 className="font-editorial text-3xl font-medium text-[#1F2D4B] flex items-center gap-2">
                        <ListMagnifyingGlass size={28} weight="duotone" className="text-[#FB7185]" />
                        Auditoría de cortes de caja
                    </h1>
                    <p className="text-sm text-[#4B5878] mt-1">
                        Listado completo de cortes generados. Haz clic en <b>Detalle</b> para ver los folios agrupados por día.
                    </p>
                </div>
                <button
                    type="button"
                    onClick={() => setShowHistorical(v => !v)}
                    data-testid="toggle-historical-report-btn"
                    className={`h-10 px-4 rounded-lg text-sm font-semibold flex items-center gap-2 transition-colors ${
                        showHistorical
                            ? 'bg-[#FB7185] text-white hover:bg-[#e15a6d]'
                            : 'border border-[#1F2D4B] text-[#1F2D4B] hover:bg-[#F8F4EC]'
                    }`}
                >
                    <Funnel size={16} weight="bold" />
                    Reporte histórico
                </button>
            </div>

            {/* Historical filter panel */}
            {showHistorical && (
                <Card className="bg-[#FDF5F5] border-[#FB7185]/30 rounded-2xl" data-testid="historical-filter-panel">
                    <CardContent className="p-4">
                        <p className="text-xs uppercase tracking-wider text-[#FB7185] font-bold mb-3">Filtros del reporte histórico</p>
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
                            <div>
                                <Label className="text-xs text-[#8B8474]">Desde</Label>
                                <Input type="date" value={histStart} onChange={(e) => setHistStart(e.target.value)}
                                    data-testid="hist-date-from" className="mt-1 h-9 text-xs" />
                            </div>
                            <div>
                                <Label className="text-xs text-[#8B8474]">Hasta</Label>
                                <Input type="date" value={histEnd} onChange={(e) => setHistEnd(e.target.value)}
                                    data-testid="hist-date-to" className="mt-1 h-9 text-xs" />
                            </div>
                            <div>
                                <Label className="text-xs text-[#8B8474]">Vendedor</Label>
                                <Select value={histSeller} onValueChange={setHistSeller}>
                                    <SelectTrigger data-testid="hist-seller-filter" className="mt-1 h-9 text-xs">
                                        <SelectValue placeholder="Todos" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="all">Todos</SelectItem>
                                        {sellers.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="flex items-end gap-2">
                                <button type="button" onClick={applyHistorical} disabled={applying}
                                    data-testid="hist-apply-btn"
                                    className="flex-1 h-9 rounded-md bg-[#1F2D4B] text-white text-xs font-bold hover:bg-[#0F1B3D] disabled:opacity-50">
                                    {applying ? 'Consultando…' : 'Consultar'}
                                </button>
                                <button type="button" onClick={clearHistorical}
                                    data-testid="hist-clear-btn"
                                    className="h-9 px-3 rounded-md border border-[#ECE3D2] text-xs text-[#4B5878] hover:bg-white">
                                    Limpiar
                                </button>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            )}

            {/* Filters */}
            <Card className="bg-white border-[#ECE3D2] rounded-2xl">
                <CardContent className="p-4">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        <div>
                            <Label className="text-xs text-[#8B8474]">Vendedor</Label>
                            <Select value={sellerFilter} onValueChange={setSellerFilter}>
                                <SelectTrigger data-testid="audit-seller-filter" className="mt-1 h-9 text-xs">
                                    <SelectValue placeholder="Todos" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">Todos</SelectItem>
                                    {sellers.map(s => (
                                        <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="md:col-span-2">
                            <Label className="text-xs text-[#8B8474]">Buscar (folio o vendedor)</Label>
                            <Input
                                placeholder="CC-000123 o nombre…"
                                value={query}
                                onChange={(e) => setQuery(e.target.value)}
                                data-testid="audit-search-input"
                                className="mt-1 h-9 text-xs"
                            />
                        </div>
                    </div>
                </CardContent>
            </Card>

            {/* Cuts table */}
            <Card className="bg-white border-[#ECE3D2] rounded-2xl overflow-hidden">
                <CardContent className="p-0">
                    <div className="flex items-center justify-between px-5 py-3 bg-[#F8F4EC] border-b border-[#ECE3D2]">
                        <h2 className="font-display text-base font-semibold text-[#1F2D4B] flex items-center gap-2">
                            <Money size={18} weight="duotone" className="text-[#FB7185]" />
                            Cortes ({filteredCuts.length}{filteredCuts.length !== cuts.length ? ` de ${cuts.length}` : ''})
                        </h2>
                    </div>
                    <div className="overflow-x-auto">
                        {loading ? (
                            <div className="py-16 text-center text-[#8B8474]">
                                <SpinnerGap size={22} className="animate-spin inline-block mr-2" />
                                Cargando cortes…
                            </div>
                        ) : (
                            <Table>
                                <TableHeader>
                                    <TableRow className="bg-white hover:bg-white border-b border-[#ECE3D2]">
                                        <TableHead className="text-xs">Folio</TableHead>
                                        <TableHead className="text-xs">Fecha</TableHead>
                                        <TableHead className="text-xs">Vendedor</TableHead>
                                        <TableHead className="text-xs text-right">Ventas</TableHead>
                                        <TableHead className="text-xs text-right">Efectivo</TableHead>
                                        <TableHead className="text-xs text-right">T. Crédito</TableHead>
                                        <TableHead className="text-xs text-right">T. Débito</TableHead>
                                        <TableHead className="text-xs text-right">Total</TableHead>
                                        <TableHead className="text-xs text-right">Diferencia</TableHead>
                                        <TableHead className="text-xs text-center">Tipo</TableHead>
                                        <TableHead className="text-xs text-right">Acciones</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {filteredCuts.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={11} className="text-center py-10 text-[#8B8474]" data-testid="empty-cuts">
                                                {cuts.length === 0 ? 'Aún no hay cortes generados.' : 'Ningún corte coincide con los filtros.'}
                                            </TableCell>
                                        </TableRow>
                                    ) : filteredCuts.map((c) => (
                                        <TableRow key={c.id} data-testid={`cut-row-${c.id}`}>
                                            <TableCell className="text-xs font-mono font-semibold text-[#1F2D4B]">{c.folio}</TableCell>
                                            <TableCell className="text-xs whitespace-nowrap">{fmtDate(c.created_at)}</TableCell>
                                            <TableCell className="text-xs text-[#4B5878]">{c.seller_name || '—'}</TableCell>
                                            <TableCell className="text-xs text-right font-mono">{c.totals?.sales_count || 0}</TableCell>
                                            <TableCell className="text-xs text-right font-mono text-[#4B5878]" data-testid={`cut-cash-${c.id}`}>{fmt(c.breakdown?.cash || 0)}</TableCell>
                                            <TableCell className="text-xs text-right font-mono text-[#4B5878]" data-testid={`cut-credit-${c.id}`}>{fmt(c.breakdown?.credit_card || 0)}</TableCell>
                                            <TableCell className="text-xs text-right font-mono text-[#4B5878]" data-testid={`cut-debit-${c.id}`}>{fmt(c.breakdown?.debit_card || 0)}</TableCell>
                                            <TableCell className="text-xs text-right font-mono font-semibold text-[#1F2D4B]">{fmt(c.totals?.total_amount || 0)}</TableCell>
                                            <TableCell className={`text-xs text-right font-mono ${Math.abs(c.cash_difference || 0) > 0.01 ? 'text-[#B91C1C] font-bold' : 'text-[#8B8474]'}`}>
                                                {Math.abs(c.cash_difference || 0) > 0.01 ? (
                                                    <span className="inline-flex items-center gap-0.5 justify-end">
                                                        <Warning size={10} weight="bold" /> {fmt(c.cash_difference)}
                                                    </span>
                                                ) : fmt(0)}
                                            </TableCell>
                                            <TableCell className="text-xs text-center">
                                                {c.is_partial ? (
                                                    <span className="text-[9px] uppercase tracking-wide bg-[#F59E0B]/15 text-[#92400E] px-1.5 py-0.5 rounded font-semibold">Parcial</span>
                                                ) : (
                                                    <span className="text-[9px] uppercase tracking-wide bg-[#ECFDF5] text-[#065F46] px-1.5 py-0.5 rounded font-semibold">Total</span>
                                                )}
                                            </TableCell>
                                            <TableCell className="text-xs text-right">
                                                <div className="inline-flex items-center gap-1">
                                                    <button
                                                        type="button"
                                                        onClick={() => setAuditCutId(c.id)}
                                                        data-testid={`audit-btn-${c.id}`}
                                                        className="h-8 px-2 rounded-md bg-[#1F2D4B] text-white hover:bg-[#0F1B3D] text-[11px] font-semibold flex items-center gap-1"
                                                        title="Ver detalle por folios"
                                                    >
                                                        <ListMagnifyingGlass size={12} weight="bold" /> Detalle
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={() => downloadCashCutPdf(c.id, c.folio)}
                                                        data-testid={`audit-pdf-${c.id}`}
                                                        className="h-8 px-2 rounded-md border border-[#ECE3D2] text-[#1F2D4B] hover:bg-[#F4ECE0] text-[11px] flex items-center gap-1"
                                                        title="Descargar PDF resumen"
                                                    >
                                                        <FileText size={12} weight="bold" />
                                                        <DownloadSimple size={10} weight="bold" />
                                                    </button>
                                                </div>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                                {filteredCuts.length > 0 && (
                                    <tfoot>
                                        <tr className="bg-[#F8F4EC] border-t-2 border-[#1F2D4B]" data-testid="cuts-totals-row">
                                            <td colSpan={4} className="px-4 py-3 text-xs font-bold text-[#1F2D4B] uppercase tracking-wide">
                                                Totales ({filteredCuts.length} corte{filteredCuts.length !== 1 ? 's' : ''})
                                            </td>
                                            <td className="px-4 py-3 text-xs text-right font-mono font-bold text-[#1F2D4B]" data-testid="totals-cash">{fmt(totals.cash)}</td>
                                            <td className="px-4 py-3 text-xs text-right font-mono font-bold text-[#1F2D4B]" data-testid="totals-credit">{fmt(totals.credit)}</td>
                                            <td className="px-4 py-3 text-xs text-right font-mono font-bold text-[#1F2D4B]" data-testid="totals-debit">{fmt(totals.debit)}</td>
                                            <td className="px-4 py-3 text-xs text-right font-mono font-bold text-[#FB7185]" data-testid="totals-total">{fmt(totals.total)}</td>
                                            <td colSpan={3} />
                                        </tr>
                                    </tfoot>
                                )}
                            </Table>
                        )}
                    </div>
                </CardContent>
            </Card>

            <CashCutAuditModal
                cutId={auditCutId}
                open={!!auditCutId}
                onClose={() => setAuditCutId(null)}
            />
        </div>
    );
};

export default CashCutsAuditPage;
